import React from 'react';
import { Typography, Box, Tooltip } from '@mui/material';
import ChartHeader from './ChartHeader';
import { fontSizes } from '../../theme/theme';
import ChartCardWrapper from './ChartCardWrapper';
import ChartLegend from './ChartLegend';

interface HeatMapDataPoint {
  [key: string]: any;
}

interface ColorLevel {
  label: string;
  color: string;
  threshold: number; // Value between 0-1 representing normalized threshold
}

interface HeatMapProps {
  title: string;
  description: string;
  data: HeatMapDataPoint[];
  height?: number;
  graphHeight?: number;
  colorScale?: {
    low: string;
    medium: string;
    high: string;
  };
  xAxisLabel?: string;
  yAxisLabel?: string;
  xAxisKey?: string;
  yAxisKey?: string;
  valueKey?: string;
  colorKey?: string;
  tooltipKey?: string;
  colorLevels?: ColorLevel[]; // Dynamic color levels configuration
  details?: any;
  onDetailsClick?: (data?: any) => void;
  gridExist?: boolean;
}

const HeatMap: React.FC<HeatMapProps> = ({
  title,
  description,
  data,
  height = 400,
  graphHeight = 400,
  colorScale = {
    low: '#E0E7FF',
    medium: '#8B5CF6',
    high: '#6D28D9'
  },
  xAxisLabel = 'Hour',
  yAxisLabel = 'Date',
  xAxisKey = 'hour',
  yAxisKey = 'date',
  valueKey = 'value',
  colorKey = 'color',
  tooltipKey = 'tooltip_text',
  colorLevels,
  details,
  onDetailsClick,
  gridExist = false
}) => {
  // Get unique axis values dynamically
  const yAxisValues = Array.from(new Set(data.map(d => d[yAxisKey]))).sort();
  const xAxisValues = Array.from(new Set(data.map(d => d[xAxisKey]))).sort((a, b) => {
    if (typeof a === 'number' && typeof b === 'number') return a - b;
    return String(a).localeCompare(String(b));
  });

  // Check if data has explicit colors
  const hasDataColors = data.length > 0 && data[0][colorKey];

  // Find min and max values for color scaling
  const values = data.map(d => d[valueKey]);
  const minValue = Math.min(...values);
  const maxValue = Math.max(...values);

  // Function to get color based on value
  const getColor = (value: number, dataColor?: string): string => {
    // If data has explicit colors, use them
    if (hasDataColors && dataColor) return dataColor;

    if (maxValue === minValue) return colorScale.medium;

    const normalized = (value - minValue) / (maxValue - minValue);

    // Use dynamic color levels if provided
    if (colorLevels && colorLevels.length > 0) {
      // Sort levels by threshold descending to find the first match
      const sortedLevels = [...colorLevels].sort((a, b) => b.threshold - a.threshold);
      for (const level of sortedLevels) {
        if (normalized >= level.threshold) {
          return level.color;
        }
      }
      return sortedLevels[sortedLevels.length - 1].color;
    }

    // Default 3-level color scale
    if (normalized < 0.33) {
      return colorScale.low;
    } else if (normalized < 0.67) {
      return colorScale.medium;
    } else {
      return colorScale.high;
    }
  };

  // Get data point for specific axis values
  const getDataPoint = (yValue: any, xValue: any): HeatMapDataPoint | null => {
    return data.find(d => d[yAxisKey] === yValue && d[xAxisKey] === xValue) || null;
  };

  // Calculate cell dimensions
  const cellWidth = 100 / (xAxisValues.length + 1); // +1 for label column
  const cellHeight = (graphHeight - 100) / (yAxisValues.length + 1); // +1 for label row

  // Handle cell click
  const handleCellClick = (yValue: any, xValue: any, dataPoint: HeatMapDataPoint | null) => {
    if (dataPoint) {
      if (onDetailsClick) {
        onDetailsClick(dataPoint);
      }
      console.log('Clicked Cell Data:');
      console.log(`${yAxisLabel}:`, yValue);
      console.log(`${xAxisLabel}:`, xValue);
      console.log('Full Data:', dataPoint);
      console.log('Value:', dataPoint[valueKey]);
    }
  };

  // Prepare legend labels
  const legendLabels = colorLevels && colorLevels.length > 0
    ? { start: colorLevels[0].label, end: colorLevels[colorLevels.length - 1].label }
    : { start: 'Low', end: 'High' };

  const legendColors = colorLevels && colorLevels.length > 0
    ? colorLevels.map(level => level.color)
    : [colorScale.low, colorScale.medium, colorScale.high];

  return (
    <ChartCardWrapper sx={{ height: height }}>
      <ChartHeader title={title} description={description} details={details} />
      <Box sx={{ position: 'relative', width: '100%', height: graphHeight, overflow: 'hidden' }}>
        <Box sx={{ display: 'flex', borderBottom: '1px solid #E5E7EB' }}>
          <Box
            sx={{
              width: `${cellWidth}%`,
              minWidth: '90px',
              padding: '8px',
              fontSize: `${fontSizes.graph.axis}px`,
              fontWeight: 600,
              color: '#6B7280',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            {yAxisLabel}/{xAxisLabel}
          </Box>
          {xAxisValues.map(xValue => (
            <Box
              key={xValue}
              sx={{
                width: `${cellWidth}%`,
                minWidth: '8px',
                padding: '8px',
                fontSize: `${fontSizes.graph.axis}px`,
                color: '#6B7280',
                textAlign: 'center',
                marginLeft: '-3px',
              }}
            >
              {xValue}
            </Box>
          ))}
        </Box>

        {/* Data rows */}
        {yAxisValues.map(yValue => (
          <Box key={yValue} sx={{ display: 'flex' }}>
            {/* Y-axis label */}
            <Box
              sx={{
                width: `${cellWidth}%`,
                minWidth: '90px',
                padding: '4px',
                fontSize: `${fontSizes.graph.axis}px`,
                fontWeight: 500,
                color: '#374151',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRight: '1px solid #E5E7EB'
              }}
            >
              {yValue}
            </Box>

            {/* Heat cells */}
            {xAxisValues.map(xValue => {
              const dataPoint = getDataPoint(yValue, xValue);
              const hasValue = dataPoint !== null;
              const value = dataPoint?.[valueKey];
              const customColor = dataPoint?.[colorKey];
              const customTooltip = dataPoint?.[tooltipKey];

              return (
                <Tooltip
                  key={`${yValue}-${xValue}`}
                  title={customTooltip || (hasValue ? `${yValue} at ${xValue} - ${value}` : 'No data')}
                  arrow
                >
                  <Box
                    onClick={() => handleCellClick(yValue, xValue, dataPoint)}
                    sx={{
                      width: `${cellWidth}%`,
                      minWidth: '10px',
                      height: `${cellHeight}px`,
                      minHeight: '32px',
                      backgroundColor: hasValue ? getColor(value, customColor) : '#F9FAFB',
                      border: '1px solid #fff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: gridExist ? 'pointer' : 'default',
                      transition: 'all 0.2s ease',
                      '&:hover': hasValue ? {
                        transform: 'scale(1.1)',
                        zIndex: 10,
                        boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                      } : {}
                    }}
                  >
                  </Box>
                </Tooltip>
              );
            })}
          </Box>
        ))}

        {/* Legend */}
        <Box sx={{ mt: 3, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
          <Typography variant="caption" sx={{ color: '#6B7280', fontWeight: 500 }}>
            {legendLabels.start}
          </Typography>
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            {legendColors.map((color, idx) => (
              <Box key={idx} sx={{ width: 24, height: 12, backgroundColor: color, borderRadius: 1 }} />
            ))}
          </Box>
          <Typography variant="caption" sx={{ color: '#6B7280', fontWeight: 500 }}>
            {legendLabels.end}
          </Typography>
        </Box>
      </Box>
      <ChartLegend 
        bottomDescription={details?.bottomDescription}
        subPoints={details?.subPoints}
        colors={['#000000']}
      />
    </ChartCardWrapper>
  );
};

export default HeatMap;
